import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import { createExercise } from '../../calendar/ducks/calendarActions';
import {
  SET_STEP,
  SET_FILE,
  DISCOVER_PROGRAMS_START,
  DISCOVER_PROGRAMS_FULFILLED,
  DISCOVER_PROGRAMS_REJECTED,
  SET_SELECTED_PROGRAM,
  EXTRACT_PROGRAM_START,
  EXTRACT_PROGRAM_PROCESSING,
  EXTRACT_PROGRAM_SLOW,
  EXTRACT_PROGRAM_FULFILLED,
  EXTRACT_PROGRAM_REJECTED,
  RESET_IMPORT,
  UPDATE_MATCH_SELECTION,
  CREATE_MATCH_EXERCISE_START,
  CREATE_MATCH_EXERCISE_FULFILLED,
  CREATE_MATCH_EXERCISE_REJECTED,
  CONFIRM_IMPORT_START,
  CONFIRM_IMPORT_FULFILLED,
  CONFIRM_IMPORT_REJECTED,
  IMPORT_STEPS,
  FETCH_IMPORT_QUOTA_START,
  FETCH_IMPORT_QUOTA_REJECTED,
  SET_IMPORT_QUOTA,
  FETCH_PENDING_IMPORTS_START,
  FETCH_PENDING_IMPORTS_FULFILLED,
  FETCH_PENDING_IMPORTS_REJECTED,
  ABANDON_IMPORT_START,
  ABANDON_IMPORT_FULFILLED,
  ABANDON_IMPORT_REJECTED,
  LOAD_IMPORT_FOR_REVIEW_START,
  LOAD_IMPORT_FOR_REVIEW_FULFILLED,
  LOAD_IMPORT_FOR_REVIEW_REJECTED,
  SET_LAST_IMPORTED_PROGRAM_ID,
  CLEAR_LAST_IMPORTED_PROGRAM_ID,
  IMPORT_HISTORY_STATUS,
  IMPORT_POLL_INTERVAL_MS,
  IMPORT_POLL_TIMEOUT_MS,
  FETCH_AI_HEALTH_START,
  FETCH_AI_HEALTH_FULFILLED,
  FETCH_AI_HEALTH_REJECTED,
} from './actionTypes';

// Stable error code returned by node-server in the 403 envelope when the
// account is over its import limit. Mirrors aiImportQuotaService/errors.js.
const QUOTA_EXCEEDED_CODE = 'QUOTA_EXCEEDED';

// Fallback shown if a Failed import row has no error_message (older rows, or
// the reaper path which sets a generic message of its own).
const EXTRACT_FAILED_FALLBACK = 'We couldn’t process this program. Please try again.';

// The import-history detail endpoint is returned in three historical shapes
// depending on the path: the extract response ({ exercises }), the legacy
// detail shape ({ exercise_matches }), and the current generated detail model
// ({ exerciseMatches }). Normalize all three to the extract shape the reducer's
// toMatchItem expects. Used by both the poll-complete path and resume-draft.
const normalizeHistoryExercises = (data = {}) => {
  const raw = data.exercises || data.exerciseMatches || data.exercise_matches || [];
  return raw.map((ex) => ({
    extractedName: ex.extractedName,
    extractedType: ex.extractedType,
    matchedExerciseId: ex.matchedExerciseId ?? ex.exerciseId ?? null,
    matchedExerciseName: ex.matchedExerciseName ?? ex.matchedName ?? null,
    matchSource: ex.matchSource ?? ex.source ?? null,
    matchConfidence: ex.matchConfidence ?? ex.confidence ?? null,
    matchConfidenceString: ex.matchConfidenceString ?? null,
  }));
};

// Try every name shape the backend has used, then fall back to a caller-
// supplied name (the value we already had from the detect/draft step).
const resolveHistoryProgramName = (data = {}, fallback = null) => data.programName
  ?? data.program_name
  ?? data.originalProgramName
  ?? data.original_program_name
  ?? fallback
  ?? null;

export const setStep = (step) => ({ type: SET_STEP, payload: step });

// Used by the home-landing path (LD flag `aiImportHomeLanding` ON). The
// build-landing path uses a toast instead and never dispatches these.
export const setLastImportedProgramId = (id) => ({
  type: SET_LAST_IMPORTED_PROGRAM_ID,
  payload: id,
});
export const clearLastImportedProgramId = () => ({ type: CLEAR_LAST_IMPORTED_PROGRAM_ID });
export const setFile = (file) => ({ type: SET_FILE, payload: file });
export const setSelectedProgram = (programName) => (
  { type: SET_SELECTED_PROGRAM, payload: programName }
);
export const resetImport = () => ({ type: RESET_IMPORT });

// Stages a coach's exercise choice for a given extracted name. The confirm call
// in step 3 uses whatever selection is staged here.
export const selectExerciseForMatch = (
  extractedName,
  exerciseId,
  exerciseName,
  { isNew = false } = {},
) => ({
  type: UPDATE_MATCH_SELECTION,
  payload: {
    extractedName, exerciseId, exerciseName, isNew,
  },
});

const getErrorMessage = (err) => err?.response?.data?.error?.message
  || err?.response?.data?.error
  || err?.response?.headers?.message
  || err.message;

// Fetch the account's current ImportQuotaState. Single source of truth for
// the "X imports remaining" pill — backend computes per-tier caps, monthly
// resets, and CSM credit balances. We never derive these client-side.
//
// Bails when accountCode is not yet hydrated (auth slice resolves async after
// app boot). The layout's mount effect re-fires once accountCode is defined.
export const fetchImportQuota = () => async (dispatch, getState) => {
  const accountCode = getState().auth?.data?.currentUser?.accountCode;
  if (!accountCode) {
    return { success: false, reason: 'NO_ACCOUNT_CODE' };
  }
  dispatch({ type: FETCH_IMPORT_QUOTA_START });
  try {
    const response = await axiosAuthed.get(
      `/accounts/${accountCode}/saved-workouts/import/quota`,
    );
    dispatch({ type: SET_IMPORT_QUOTA, payload: response.data });
    return { success: true, quota: response.data };
  } catch (err) {
    dispatch({
      type: FETCH_IMPORT_QUOTA_REJECTED,
      payload: getErrorMessage(err),
    });
    return { success: false };
  }
};

// Liveness gate for the upload step. GET /healthcheck/ai-providers returns
//   { anthropic: { status, reason? }, openai: { status, reason? },
//     available: boolean, checkedAt: ISO }
// where `available` is true only when BOTH providers respond (results cached
// 60s server-side). Fail OPEN: on a check error we leave aiHealth untouched and
// return success:false so callers don't block — the extract job's own Failed
// path still catches a real outage. Not account-scoped (top-level route).
export const fetchAiProviderHealth = () => async (dispatch) => {
  dispatch({ type: FETCH_AI_HEALTH_START });
  try {
    const response = await axiosAuthed.get('/healthcheck/ai-providers');
    dispatch({ type: FETCH_AI_HEALTH_FULFILLED, payload: response.data });
    return { success: true, health: response.data };
  } catch (err) {
    dispatch({ type: FETCH_AI_HEALTH_REJECTED, payload: getErrorMessage(err) });
    return { success: false };
  }
};

export const discoverPrograms = (file) => async (dispatch, getState) => {
  dispatch({ type: DISCOVER_PROGRAMS_START });
  try {
    const { accountCode } = getState().auth.data.currentUser;
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosAuthed.post(
      `/accounts/${accountCode}/saved-workouts/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    dispatch({
      type: DISCOVER_PROGRAMS_FULFILLED,
      payload: { sessionId: response.data.sessionId, programs: response.data.programs },
    });
    dispatch(setStep(IMPORT_STEPS.DETECT));
  } catch (err) {
    // 403 QUOTA_EXCEEDED carries the fresh ImportQuotaState in the body —
    // capture it so the uploader pill shows the real "0 left, resets DATE"
    // without an extra round-trip. The route handler's shape is
    //   { error, code: 'QUOTA_EXCEEDED', quota }
    // (see node-server/.../saved-workouts/import.js).
    const data = err?.response?.data;
    if (data?.code === QUOTA_EXCEEDED_CODE && data.quota) {
      dispatch({ type: SET_IMPORT_QUOTA, payload: data.quota });
    }
    dispatch({
      type: DISCOVER_PROGRAMS_REJECTED,
      payload: getErrorMessage(err),
    });
  }
};

// Step 2: kick off extract + match. The AI round-trips (extraction + matching)
// run longer than the 120s load-balancer timeout, so the backend no longer
// does the work in this request. It creates an Import_History row in
// Processing state, launches a background job, and returns 202 with just
// { programImportId }. The actual matches arrive later via startImportPolling.
//
// This decouples the UI from the AI provider's latency: if Anthropic/OpenAI is
// down the request can't hang, because there's nothing to wait on — the job
// flips the row to Failed and the poll surfaces it. (Previously this was a
// single multi-minute await that left the spinner stuck forever on an
// upstream stall.)
export const extractProgram = (sessionId, programName) => async (dispatch, getState) => {
  dispatch({ type: EXTRACT_PROGRAM_START });
  try {
    const { accountCode } = getState().auth.data.currentUser;

    const response = await axiosAuthed.post(
      `/accounts/${accountCode}/saved-workouts/import/${sessionId}`,
      { programName },
    );

    // The 202 response field was renamed importHistoryId -> programImportId
    // (node-server sc-25203, the Import_History -> Program_Imports rename). Read
    // the new name first, falling back to the old one so we keep working against
    // backends that haven't shipped the rename yet.
    const importHistoryId = response.data.programImportId ?? response.data.importHistoryId;
    if (importHistoryId == null) {
      throw new Error(EXTRACT_FAILED_FALLBACK);
    }

    // Hand the id to StepProcessing, which owns the poll loop (so it's tied to
    // that screen's mount lifecycle and cancels cleanly on unmount).
    dispatch({ type: EXTRACT_PROGRAM_PROCESSING, payload: { importHistoryId } });
    // Quota is deducted server-side the moment the row is created (tagged
    // plan/credit/trial), not at confirm. Re-fetch so the pill reflects the
    // new usedThisMonth / creditsRemaining without a page refresh. On a Failed
    // outcome the backend refunds the credit, so we re-fetch again there too.
    dispatch(fetchImportQuota());
    return { success: true, importHistoryId };
  } catch (err) {
    // POST itself failed (bad session, quota, network) — fall straight back to
    // the program-select step with the message inline, same as a Failed poll.
    dispatch({
      type: EXTRACT_PROGRAM_REJECTED,
      payload: getErrorMessage(err),
    });
    dispatch(setStep(IMPORT_STEPS.DETECT));
    return { success: false };
  }
};

// Polls GET .../import/history/:historyId until the background extract job
// leaves the Processing state, then drives the wizard:
//   Pending (0) → hydrate the Review step from exerciseMatches (same shape the
//                 resume-draft path uses) and advance to step 4.
//   Failed (2)  → drop back to the program-select step with errorMessage shown
//                 inline (the coach can re-pick and retry; quota was refunded).
//   still Processing past IMPORT_POLL_TIMEOUT_MS → stop and show the "taking a
//                 while, check your drafts" state. The job keeps running
//                 server-side and surfaces as a Pending draft if it finishes.
//
// Returns a cancel() function so the caller (StepProcessing) can stop the loop
// on unmount. Transient GET errors are swallowed and retried until the timeout
// — a blip shouldn't abort an import that's still running.
export const startImportPolling = (historyId) => (dispatch, getState) => {
  let cancelled = false;
  let timer = null;
  const deadline = Date.now() + IMPORT_POLL_TIMEOUT_MS;

  const finishWithReview = (data) => {
    dispatch({
      type: EXTRACT_PROGRAM_FULFILLED,
      payload: {
        importHistoryId: data.importHistoryId ?? data.id ?? historyId,
        exercises: normalizeHistoryExercises(data),
      },
    });
    const programName = resolveHistoryProgramName(data, getState().aiProgramImport.selectedProgram);
    if (programName) dispatch(setSelectedProgram(programName));
    dispatch(setStep(IMPORT_STEPS.REVIEW));
  };

  const failBackToDetect = (message) => {
    dispatch({ type: EXTRACT_PROGRAM_REJECTED, payload: message || EXTRACT_FAILED_FALLBACK });
    dispatch(setStep(IMPORT_STEPS.DETECT));
    // Credit is refunded server-side on failure; refresh the pill.
    dispatch(fetchImportQuota());
  };

  const poll = async () => {
    if (cancelled) return;
    const accountCode = getState().auth?.data?.currentUser?.accountCode;
    if (!accountCode) return;
    try {
      const response = await axiosAuthed.get(
        `/accounts/${accountCode}/saved-workouts/import/history/${historyId}`,
      );
      if (cancelled) return;
      const data = response.data || {};

      if (data.status === IMPORT_HISTORY_STATUS.Pending
        || data.status === IMPORT_HISTORY_STATUS.Success) {
        finishWithReview(data);
        return;
      }
      if (data.status === IMPORT_HISTORY_STATUS.Failed
        || data.status === IMPORT_HISTORY_STATUS.Abandoned) {
        failBackToDetect(data.errorMessage || data.error_message);
        return;
      }
      // Still Processing (4). Stop once we've waited long enough — the job may
      // still complete server-side and show up in the drafts list.
      if (Date.now() >= deadline) {
        dispatch({ type: EXTRACT_PROGRAM_SLOW });
        return;
      }
      timer = setTimeout(poll, IMPORT_POLL_INTERVAL_MS);
    } catch (err) {
      if (cancelled) return;
      // Transient failure — keep trying until the deadline, then surface the
      // slow state rather than a hard error (the import isn't necessarily dead).
      if (Date.now() >= deadline) {
        dispatch({ type: EXTRACT_PROGRAM_SLOW });
        return;
      }
      timer = setTimeout(poll, IMPORT_POLL_INTERVAL_MS);
    }
  };

  poll();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
};

// Creates a new exercise on the coach's account and stages it as the selection
// for the given extracted name. No swap call — the program doesn't exist yet.
export const createExerciseForMatch = (
  extractedName,
  name,
  type,
  trackFields = {},
) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: CREATE_MATCH_EXERCISE_START });
  return new Promise((resolve) => {
    dispatch(createExercise(accountCode, { name, type, ...trackFields }, {
      silent: true,
      onSuccess: (response) => {
        dispatch(selectExerciseForMatch(
          extractedName,
          response.id,
          response.name,
          { isNew: true },
        ));
        dispatch({ type: CREATE_MATCH_EXERCISE_FULFILLED });
        resolve({ success: true, exerciseId: response.id, exerciseName: response.name });
      },
      onError: (errMsg) => {
        dispatch({ type: CREATE_MATCH_EXERCISE_REJECTED, payload: errMsg });
        resolve({ success: false });
      },
    }));
  });
};

// Step 3: confirm staged selections and have the backend build the program.
export const confirmImport = () => async (dispatch, getState) => {
  const state = getState().aiProgramImport;
  const { importHistoryId, matches, selectedProgram } = state;
  if (!importHistoryId) return { success: false };

  const { accountCode } = getState().auth.data.currentUser;
  // selectedProgram may have been edited on the review step. We forward it
  // so the backend can use the (possibly renamed) value when creating the
  // saved workout. Backends that ignore it will fall back to the name
  // captured at extract time.
  const payload = {
    programName: selectedProgram || undefined,
    exercises: matches
      .filter((m) => m.selectedExerciseId != null)
      .map((m) => ({
        extractedName: m.extractedName,
        selectedExerciseId: m.selectedExerciseId,
      })),
  };

  dispatch({ type: CONFIRM_IMPORT_START });
  try {
    const response = await axiosAuthed.post(
      `/accounts/${accountCode}/saved-workouts/import/history/${importHistoryId}/confirm`,
      payload,
    );
    const { created = [], failed = [] } = response.data;
    dispatch({
      type: CONFIRM_IMPORT_FULFILLED,
      payload: { created, failed },
    });
    return { success: true, created, failed };
  } catch (err) {
    dispatch({
      type: CONFIRM_IMPORT_REJECTED,
      payload: getErrorMessage(err),
    });
    return { success: false };
  }
};

// Resume-import surface — used by the Program navigator's Unfinished Imports
// group. The list endpoint accepts a status filter; we only ever request
// pending here. Detail returns the same shape as extract so we can hydrate
// the wizard and drop the coach back at step 4 (Review).

// Program_Imports.created_at is a Unix timestamp in SECONDS (INT(11)) as of
// node-server sc-25203; older backends sent an ISO date-time string. Normalize
// both to a millisecond epoch so the drafts list's relative-age label and sort
// (StepUploader, both of which feed `new Date(...)`) get a correct value — a
// raw seconds value passed to `new Date` is read as ms and lands in Jan 1970.
const toEpochMs = (value) => {
  if (value == null) return null;
  // Numeric (or numeric-string) value === Unix seconds from the new backend.
  if (typeof value === 'number') return value * 1000;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value) * 1000;
  // Anything else is an ISO date-time string (legacy backend).
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// Normalize a history-list row into the camelCase shape the rest of the app
// reads. The list endpoint (ImportHistoryListItem.fromDbReturn in node-server)
// already serializes camelCase — programName, originalFilename, createdAt, etc.
// — so we read camelCase FIRST and keep snake_case only as a defensive
// fallback. Reading snake_case first silently dropped programName + createdAt
// (the draft rows lost their title and date) since those keys don't exist on
// the wire; only fileName survived because it already had the camelCase fallback.
const normalizePendingImport = (it) => ({
  id: it.id,
  accountCode: it.accountCode ?? it.account_code ?? null,
  saveInfoId: it.saveInfoId ?? it.save_info_id ?? null,
  // Uploaded spreadsheet filename — the draft row's headline in the Step 1
  // "Past imports waiting for review:" card (Figma node 7230:11081).
  fileName: it.originalFilename ?? it.original_filename ?? null,
  programName: it.programName ?? it.program_name ?? null,
  originalProgramName: it.originalProgramName ?? it.original_program_name ?? null,
  status: it.status,
  totalRowsExtracted: it.totalRowsExtracted ?? it.total_rows_extracted ?? null,
  exercisesMatched: it.exercisesMatched ?? it.exercises_matched ?? null,
  estimatedCost: it.estimatedCost ?? it.estimated_cost ?? null,
  quotaSource: it.quotaSource ?? it.quota_source ?? null,
  consumedGrantId: it.consumedGrantId ?? it.consumed_grant_id ?? null,
  createdAt: toEpochMs(it.createdAt ?? it.created_at),
});

export const fetchPendingImports = () => async (dispatch, getState) => {
  const accountCode = getState().auth?.data?.currentUser?.accountCode;
  if (!accountCode) return { success: false };
  dispatch({ type: FETCH_PENDING_IMPORTS_START });
  try {
    const response = await axiosAuthed.get(
      `/accounts/${accountCode}/saved-workouts/import/history?status=pending`,
    );
    const { data } = response;
    const raw = Array.isArray(data) ? data : (data?.imports || data?.history || []);
    const imports = raw.map(normalizePendingImport);
    dispatch({ type: FETCH_PENDING_IMPORTS_FULFILLED, payload: imports });
    return { success: true, imports };
  } catch (err) {
    dispatch({
      type: FETCH_PENDING_IMPORTS_REJECTED,
      payload: getErrorMessage(err),
    });
    return { success: false };
  }
};

export const abandonImport = (historyId) => async (dispatch, getState) => {
  const accountCode = getState().auth?.data?.currentUser?.accountCode;
  if (!accountCode || !historyId) return { success: false };
  dispatch({ type: ABANDON_IMPORT_START });
  try {
    await axiosAuthed.put(
      `/accounts/${accountCode}/saved-workouts/import/history/${historyId}/abandon`,
    );
    dispatch({ type: ABANDON_IMPORT_FULFILLED, payload: { historyId } });
    return { success: true };
  } catch (err) {
    dispatch({
      type: ABANDON_IMPORT_REJECTED,
      payload: getErrorMessage(err),
    });
    return { success: false };
  }
};

// Pulls a Pending import back into the wizard. Hydrates the same fields the
// extract step populates, then sets currentStep to 4 so the Review screen
// renders without a fresh upload + extract round-trip.
// `fallbackName` is the program name the caller already has from the pending
// list — used when the detail endpoint omits the program name (the list and
// detail endpoints don't always agree on snake/camel casing or which fields
// they expose), so the Review step's program-name field still auto-fills.
export const loadImportForReview = (
  historyId,
  fallbackName = null,
) => async (dispatch, getState) => {
  const accountCode = getState().auth?.data?.currentUser?.accountCode;
  if (!accountCode || !historyId) return { success: false };
  // Wipe any post-confirm state from a prior import in this session before
  // hydrating. The wizard layout's mount effect resets when createdPrograms
  // is non-empty — without this, the layout would clobber our resumed state.
  dispatch({ type: RESET_IMPORT });
  dispatch({ type: LOAD_IMPORT_FOR_REVIEW_START });
  try {
    const response = await axiosAuthed.get(
      `/accounts/${accountCode}/saved-workouts/import/history/${historyId}`,
    );
    const data = response.data || {};
    dispatch({
      type: LOAD_IMPORT_FOR_REVIEW_FULFILLED,
      payload: {
        importHistoryId: data.importHistoryId ?? data.id ?? historyId,
        programName: resolveHistoryProgramName(data, fallbackName),
        exercises: normalizeHistoryExercises(data),
      },
    });
    return { success: true };
  } catch (err) {
    dispatch({
      type: LOAD_IMPORT_FOR_REVIEW_REJECTED,
      payload: getErrorMessage(err),
    });
    return { success: false };
  }
};
